import React from 'react';
import { ProductContent } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelContentAdapter';
import { ChartDataAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  ContentCardContainer,
  ContentCardHeader,
  ContentTextContainer
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/components/UtilizationCardContainer';

import Papa from 'papaparse';
import saveAs from 'file-saver';
import { useMainTimePeriod } from 'src/utils/Hooks';
import { useProductEntity, useRetailerDisplayName, useRetailerSku } from 'src/utils/Hooks/reduxSelectors';

const EXPORT_HEADERS = [
  'Retailer ID',
  'Retailer Name',
  'Retailer SKU',
  'UPC',
  'Product Title',
  'Brand',
  'Category',
  'Subcategory',
  'Availability Status',
  'Availability Status Code',
  'Title',
  'Bullet 1',
  'Bullet 2',
  'Bullet 3',
  'Bullet 4',
  'Bullet 5',
  'Bullet 6',
  'Bullet 7',
  'Bullet 8',
  'Bullet 9',
  'Bullet 10',
  'Description'
];
interface ProductLevelVoiceAndToneContentDetailsProps {
  title: string;
  queryFn: () => Promise<ChartDataAdapter<ProductContent>>;
  renderSubtitle: (data: ProductContent) => string | React.ReactNode;
}

const Bullets = ({ bullets }: { bullets: string[] | undefined }) => {
  const actualBullets = bullets?.length || 0;

  return (
    <>
      {[...Array(actualBullets)].map((_, i) => {
        const bullet = i < actualBullets ? bullets?.[i] : undefined;
        const title = `Bullet ${i + 1}`;
        return (
          <ContentCardContainer key={title} sx={{ padding: '24px 16px' }}>
            <ContentCardHeader title={title}></ContentCardHeader>
            <ContentTextContainer textSx={{ fontSize: '16px', lineHeight: '24px' }}>
              {bullet ?? 'Missing required bullet.'}
            </ContentTextContainer>
          </ContentCardContainer>
        );
      })}
    </>
  );
};

export const ProductLevelVoiceAndToneContentDetails = (props: ProductLevelVoiceAndToneContentDetailsProps) => {
  const { title, queryFn, renderSubtitle } = props;
  const { endWeek } = useMainTimePeriod();
  const retailerSku = useRetailerSku();
  const retailerName = useRetailerDisplayName();
  const { productEntity } = useProductEntity();

  const generateExcelExport = (data) => {
    const contentData = data.transform();

    const exportRows = [
      {
        'Retailer ID': contentData.retailerId || '',
        'Retailer Name': retailerName,
        'Retailer SKU': contentData?.retailerSku || retailerSku || '',
        UPC: productEntity?.upc || '',
        'Product Title': productEntity?.name || productEntity?.title || '',
        Brand: productEntity?.brandName || '',
        Category: productEntity?.categoryName || '',
        Subcategory: productEntity?.subCategoryName || '',
        'Availability Status': productEntity?.availabilityStatus || '',
        'Availability Status Code': productEntity?.availabilityStatusCode || '',
        Title: contentData?.title || ''
      }
    ];

    // Dynamically add bullets based on what's available
    if (contentData.bullets && contentData.bullets.length > 0) {
      // Add bullets up to the number available (only fill the ones that exist)
      contentData.bullets.forEach((bullet, index) => {
        exportRows[0][`Bullet ${index + 1}`] = bullet || '';
      });

      // If there are fewer than 10 bullets, make sure we don't reference undefined ones
      for (let i = contentData.bullets.length + 1; i <= 10; i++) {
        exportRows[0][`Bullet ${i}`] = '';
      }
    } else {
      // If no bullets at all, fill in empty strings for all 10 bullet fields
      for (let i = 1; i <= 10; i++) {
        exportRows[0][`Bullet ${i}`] = '';
      }
    }
    // eslint-disable-next-line dot-notation
    exportRows[0]['Description'] = contentData.description || '';

    try {
      const csv = Papa.unparse({
        fields: EXPORT_HEADERS,
        data: exportRows
      });
      const csvWithBOM = `\ufeff${csv}`;
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `Product_${retailerSku}_VoiceTone_${endWeek}.csv`);
    } catch (e) {
      console.error('Error generating export:', e);
    }
  };

  return (
    <BeaconProChart
      name={`voice-and-tone-content-details-${title.toLowerCase().replace(/\s+/g, '-')}`}
      queryFn={queryFn}
    >
      <BeaconProChart.Header
        menuButton={(data, isLoading) => {
          return (
            <SlButton disabled={isLoading} onClick={() => generateExcelExport(data)}>
              Export
            </SlButton>
          );
        }}
      >
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(adapter: ChartDataAdapter<ProductContent>) => renderSubtitle(adapter.transform())}
      />

      <BeaconProChart.Visualization
        render={(data: ChartDataAdapter<ProductContent>) => {
          const contentData = data.transform();

          return (
            <Flex marginTop="32px" flexDirection="column" gap={32}>
              <ContentCardContainer sx={{ padding: '24px 16px' }}>
                <ContentCardHeader title="Title"></ContentCardHeader>
                <ContentTextContainer textSx={{ fontSize: '16px', lineHeight: '24px' }}>
                  {contentData.title ?? 'This product has no title.'}
                </ContentTextContainer>
              </ContentCardContainer>

              <Bullets bullets={contentData.bullets} />

              <ContentCardContainer sx={{ padding: '24px 16px' }}>
                <ContentCardHeader title="Description"></ContentCardHeader>
                <ContentTextContainer textSx={{ fontSize: '16px', lineHeight: '24px' }}>
                  {contentData?.description || 'This product has no description.'}
                </ContentTextContainer>
              </ContentCardContainer>
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
